import { validateUnitSerial } from '@siliconx/shared/dto/asset-unit';

/**
 * SIL-1917: filter for barcode decode results captured against a stock line.
 * Dell labels carry the service tag (5-7 chars) as Code 128 or inside a QR
 * support URL, next to the express service code (10-11 digits, numeric only).
 * The express code is a different number and must never be saved as a serial.
 */

export type DecodeVerdict =
  | { kind: 'serial'; value: string }
  | { kind: 'express_code' }
  | { kind: 'invalid' };

const EXPRESS_CODE = /^\d{10,11}$/;
const TAG_PARAMS = ['servicetag', 'service_tag', 'st', 'tag'];

/** Pull the service tag out of a Dell support QR URL, else return the raw text. */
function unwrapQr(raw: string): string {
  if (!/^https?:\/\//i.test(raw)) return raw;
  try {
    const url = new URL(raw);
    for (const [k, v] of url.searchParams) {
      if (TAG_PARAMS.includes(k.toLowerCase()) && v.trim() !== '') return v.trim();
    }
    // Dell short links: https://qrcode.dell.com/<TAG>
    const last = url.pathname.split('/').filter(Boolean).pop();
    if (last) return last;
  } catch {
    /* fall through to raw */
  }
  return raw;
}

export function classifyDecodedValue(raw: string): DecodeVerdict {
  const value = unwrapQr(raw.trim()).trim();
  if (EXPRESS_CODE.test(value)) return { kind: 'express_code' };
  if (validateUnitSerial(value) !== null) return { kind: 'invalid' };
  return { kind: 'serial', value };
}

/** First usable serial among everything one frame decoded; else why none qualified. */
export function pickSerial(rawValues: string[]): DecodeVerdict | null {
  if (rawValues.length === 0) return null;
  let sawExpress = false;
  for (const raw of rawValues) {
    const v = classifyDecodedValue(raw);
    if (v.kind === 'serial') return v;
    if (v.kind === 'express_code') sawExpress = true;
  }
  return sawExpress ? { kind: 'express_code' } : { kind: 'invalid' };
}
